import React, { useState, useEffect } from "react";
import { Form, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import Checkbox from "antd/es/checkbox/Checkbox";

import notificationService from "@/util/notificationService";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
} from "@/util/common_function";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";

import {
  StyledGridCol,
  StyledGridRow,
  StyledTypographyText,
} from "@/common/styles";
import { BookingsSectionTitle } from "./styles";
import ScheduledEventsInspections from "../ScheduledEventsInspections";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const ScheduledEventsDetails = () => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );
  const [inspections, setInspections] = useState([]);
  const { userDetail } = useSelector((s) => s?.userDetail);

  // Keep local array of scheduledEvents
  const [localScheduledEvents, setLocalScheduledEvents] = useState([]);

  useEffect(() => {
    setLocalScheduledEvents(bookings?.scheduledEvents || []);
  }, [bookings?.scheduledEvents]);

  // Local UI states
  const [showScheduledEvents, setShowScheduledEvents] = useState(false);
  const [modalData, setModalData] = useState(null); // store the row being edited
  const [isEditing, setIsEditing] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  // Create form instance for AntD Form
  const [form] = Form.useForm();
  const fullSearch = useSelector(state => state.fullSearch);

  // Fetch DaysOfWeek reference dynamically
  const daysOfWeekOptions = getOptionsFromRefList(
    "DaysOfWeek",
    false,
    false,
    true
  );

  // Create dynamic dayOfWeekMap from fetched data
  const dayOfWeekMap = daysOfWeekOptions.reduce((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});

  /** Reverse-map from day name to dayOfWeekID */
  function getDayOfWeekID(dayName) {
    const entry = Object.entries(dayOfWeekMap).find(
      ([id, name]) => name === dayName
    );
    return entry ? parseInt(entry[0], 10) : null;
  }

  function getScheduledEventKey(item) {
    if (item?.scheduledEventID) {
      return item.scheduledEventID; // From API
    }
    return item?.id; // Local fallback
  }
  // Watch form fields
  const isRecurring = Form.useWatch("recurring", form);
  const isActiveRecurring = Form.useWatch("activeRecurring", form);

  // Also watch "eventsType" to conditionally show business info
  const scheduledEventOptions = getOptionsFromRefList(
    "Scheduled_Event_Type",
    false,
    false,
    true
  );
  const eventTypeValue = Form.useWatch("scheduledEventTypeID", form);

  // Get label for selected Event Type
  const selectedEventTypeLabel = scheduledEventOptions.find(
    (opt) => opt.value === eventTypeValue
  )?.label;
  const isJobSchedule = selectedEventTypeLabel === "Job Schedule";

  // Auto-check and disable "recurring" for Job Schedule
  useEffect(() => {
    if (isJobSchedule) {
      form.setFieldsValue({ recurring: true });
    }
  }, [isJobSchedule, form]);

  const clickScheduledEvents = () => {
    setModalData(null);
    setIsEditing(false);
    setShowScheduledEvents(true);
    form.resetFields();
    setInspections([]);
    form.setFieldsValue({
      oneTimeDateTime: "",
      startDate: !fullSearch ? dayjs() : null,
    });
  };

  const onRowClick = (row) => {
    setModalData(row);
  };

  const handleEdit = () => {
    if (!modalData) {
      notificationService.warning("Please select a row to edit.");
      return;
    }

    // Rebuild a single oneTimeDateTime from stored oneTimeDate + oneTime
    let combinedDateTime = null;
    if (modalData?.oneTimeDate && modalData?.oneTime) {
      combinedDateTime = dayjs(
        `${modalData.oneTimeDate} ${modalData.oneTime}`,
        "MM/DD/YYYY HH:mm"
      );
    }

    // Extract scheduledEventsExtra values if present
    const extra = modalData?.scheduledEventsExtra?.[0] || {};

    // If we have recurring data from the API, parse it
    const recurringData = modalData?.scheduledEventsRecurring?.[0] || {};

    // Check event type label based on the item’s scheduledEventTypeID
    const currentEventTypeLabel = scheduledEventOptions.find(
      (opt) => opt.value === modalData?.scheduledEventTypeID
    )?.label;
    const isActuallyJobSchedule = currentEventTypeLabel === "Job Schedule";

    // Parse recurring dates
    const startDateVal = recurringData.startDate
      ? dayjs(recurringData.startDate, "MM/DD/YYYY")
      : null;
    const endDateVal = recurringData.endDate
      ? dayjs(recurringData.endDate, "MM/DD/YYYY")
      : null;
    const reactivationDateVal = recurringData.reactivationDate
      ? dayjs(recurringData.reactivationDate, "MM/DD/YYYY")
      : null;

    // Build day-by-day
    const daysObj = {
      Sunday: { active: false, startTime: null, endTime: null },
      Monday: { active: false, startTime: null, endTime: null },
      Tuesday: { active: false, startTime: null, endTime: null },
      Wednesday: { active: false, startTime: null, endTime: null },
      Thursday: { active: false, startTime: null, endTime: null },
      Friday: { active: false, startTime: null, endTime: null },
      Saturday: { active: false, startTime: null, endTime: null },
    };

    if (recurringData?.scheduledEventsRecurringSchedule?.length) {
      recurringData.scheduledEventsRecurringSchedule.forEach((sched) => {
        const dayName = dayOfWeekMap[parseInt(sched.dayofWeekID, 10)];
        if (dayName && daysObj[dayName]) {
          daysObj[dayName] = {
            active: !!sched.active,
            startTime: sched.startTime ? dayjs(sched.startTime, "HH:mm") : null,
            endTime: sched.endTime ? dayjs(sched.endTime, "HH:mm") : null,
          };
        }
      });
    }

    // READ "workReleaseDestinations" if it's a Job Schedule
    let businessNameVal = "";
    let businessHouseNumberVal = "";
    let businessStreetVal = "";
    let businessStreet2Val = "";
    let businessAptVal = "";
    let businessCityVal = "";
    let businessStateVal = "";
    let businessZipVal = "";
    let businessPhoneVal = "";
    let businessFaxVal = "";

    // To REUSE existing IDs on save, store them on local references:
    let existingWRDId = null;
    let existingBusId = null;
    let existingBusDetailsId = null;
    let existingAddressId = null;

    if (isActuallyJobSchedule && recurringData.workReleaseDestinations) {
      const wrd = recurringData.workReleaseDestinations[0] || {};
      existingWRDId = wrd.workReleaseDestinationID || null;

      const bus = wrd.businesses?.[0] || {};
      existingBusId = bus.businessId || null;

      const busDet = bus.businessDetails?.[0] || {};
      existingBusDetailsId = busDet.businessDetailsId || null;

      const addr = bus.addresses?.[0] || {};
      existingAddressId = addr.addressId || null;

      businessNameVal = bus.businessName || "";
      businessHouseNumberVal = addr.addressStreetNumber || "";
      businessStreetVal = addr.addressStreet1 || "";
      businessStreet2Val = addr.addressStreet2 || "";
      businessAptVal = addr.addressApartmentNumber || "";
      businessCityVal = addr.addressCity || "";
      businessStateVal = addr.addressState || "";
      businessZipVal = addr.addressZip || "";
      businessPhoneVal = bus.businessPhone || "";
      businessFaxVal = bus.businessFax || "";
    }

    const existingInspections =
      recurringData?.workReleaseDestinations?.[0]?.workReleaseInspections || [];
    setInspections(existingInspections);

    // Now set form fields
    form.setFieldsValue({
      ...modalData,
      activeRecurring: !!recurringData.active,
      oneTimeDateTime: combinedDateTime,
      oneTimeEventFinishes: modalData?.oneTimeEventFinishes
        ? dayjs(modalData.oneTimeEventFinishes, "HH:mm")
        : null,
      scheduledEventExtraID: extra.scheduledEventExtraID || null,
      scheduledEventID: extra.scheduledEventID || null,
      signOutInmateU: !!extra.signOutInmateU,
      startDate: startDateVal,
      endDate: endDateVal,
      reactivationDate: reactivationDateVal,
      reactivationComments: recurringData.reactivationComments || "",
      Sunday: daysObj.Sunday,
      Monday: daysObj.Monday,
      Tuesday: daysObj.Tuesday,
      Wednesday: daysObj.Wednesday,
      Thursday: daysObj.Thursday,
      Friday: daysObj.Friday,
      Saturday: daysObj.Saturday,
      // Fill business fields
      businessName: businessNameVal,
      businessHouseNumber: businessHouseNumberVal,
      businessStreet: businessStreetVal,
      businessStreet2: businessStreet2Val,
      businessApt: businessAptVal,
      businessCity: businessCityVal,
      businessState: businessStateVal,
      businessZip: businessZipVal,
      businessPhone: businessPhoneVal,
      businessFax: businessFaxVal,
      // Also store the existing IDs in hidden fields so we can reuse them
      existingWRDId,
      existingBusId,
      existingBusDetailsId,
      existingAddressId,
    });

    setIsEditing(true);
    setShowScheduledEvents(true);
  };

  /** Delete existing row and trigger the deletion API */
  const handleDelete = () => {
    if (!modalData) {
      notificationService.warning("Please select a row to delete.");
      return;
    }
    const keyToDelete = getScheduledEventKey(modalData);
    const updated = localScheduledEvents.filter((item) => {
      const itemKey = getScheduledEventKey(item);
      return itemKey !== keyToDelete;
    });

    if (typeof keyToDelete === "number") {
      const url = API_ENDPOINTS.scheduledEvents + keyToDelete;
      dispatch(setDeleteAPis(url));
    }

    setLocalScheduledEvents(updated);
    dispatch(setBookings({ ...bookings, scheduledEvents: updated }));
    setModalData(null);
  };

  /** Save (Add / Modify) */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Separate oneTimeDateTime into date + time
      let oneTimeDate = null;
      let oneTime = null;
      if (values.oneTimeDateTime) {
        if(values.travelTime) {
          oneTimeDate = dayjs(values.oneTimeDateTime).subtract(values.travelTime, "minute").format("MM/DD/YYYY");
          oneTime = dayjs(values.oneTimeDateTime).subtract(values.travelTime, "minute").format("HH:mm");
        }
        else {
          oneTimeDate = dayjs(values.oneTimeDateTime).format("MM/DD/YYYY");
          oneTime = dayjs(values.oneTimeDateTime).format("HH:mm");
        }
      }
      
      // Build scheduledEventsExtra array with signOut info
      const scheduledEventsExtra = [
        {
          scheduledEventExtraID: values.scheduledEventExtraID || null,
          scheduledEventID: values.scheduledEventID || null,
          signOutInmateU: !!values.signOutInmateU,
        },
      ];

      // Recurring date fields
      const startDateString = values.startDate
        ? dayjs(values.startDate).format("MM/DD/YYYY")
        : null;
      const endDateString = values.endDate
        ? dayjs(values.endDate).format("MM/DD/YYYY")
        : null;
      const reactivationDateString = values.reactivationDate
        ? dayjs(values.reactivationDate).format("MM/DD/YYYY")
        : null;

      // Put them all together
      let formattedValues = {
        ...values,
        scheduledEventsExtra,
        oneTimeDate,
        oneTime,
        oneTimeEventFinishes: values.oneTimeEventFinishes
          ? (values.travelTime ? 
            dayjs(values.oneTimeEventFinishes).add(values.travelTime, "minute").format("HH:mm") :
            dayjs(values.oneTimeEventFinishes).format("HH:mm"))
          : null,
        startDate: startDateString,
        endDate: endDateString,
        reactivationDate: reactivationDateString,
      };

      let hTOneTimeDate = formattedValues.startDate ?? formattedValues.oneTimeDate;
      let hTBkTimeDate = formattedValues.startDate ?? formattedValues.oneTimeDate;
      let hTOneTime = null;
      let hTBkTime = null;

      // Remove single-use fields from top-level
      delete formattedValues.oneTimeDateTime;
      delete formattedValues.signOutInmateU;
      delete formattedValues.scheduledEventID;
      delete formattedValues.scheduledEventExtraID;

      // Also remove the hidden ID fields from top-level:
      const existingWRDId = formattedValues.existingWRDId;
      const existingBusId = formattedValues.existingBusId;
      const existingBusDetailsId = formattedValues.existingBusDetailsId;
      const existingAddressId = formattedValues.existingAddressId;
      delete formattedValues.existingWRDId;
      delete formattedValues.existingBusId;
      delete formattedValues.existingBusDetailsId;
      delete formattedValues.existingAddressId;

      // Build out recurring data if "recurring" is true
      // Ensure existing IDs are preserved on edit
      let scheduledEventsRecurring = [];
      if (values.recurring) {
        const recurringObj = {
          scheduledEventRecurringID:
            modalData?.scheduledEventsRecurring?.[0]
              ?.scheduledEventRecurringID || null,
          active: !!values.activeRecurring,
          startDate: startDateString,
          endDate: endDateString,
          reactivationDate: reactivationDateString,
          reactivationComments: values.reactivationComments || "",
          scheduledEventsRecurringSchedule: [],
        };

        // Day-of-week schedule
        [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ].forEach((dayName) => {
          const dayField = values[dayName];
          if (dayField?.active) {
            recurringObj.scheduledEventsRecurringSchedule.push({
              scheduledEventRecurringScheduleID:
                modalData?.scheduledEventsRecurring?.[0]?.scheduledEventsRecurringSchedule?.find(
                  (r) => getDayOfWeekID(dayName) === r.dayofWeekID
                )?.scheduledEventRecurringScheduleID || null,
              scheduledEventRecurringID: recurringObj.scheduledEventRecurringID,
              active: true,
              dayofWeekID: getDayOfWeekID(dayName),
              startTime: dayField.startTime
                ? dayjs(dayField.startTime).format("HH:mm")
                : null,
              endTime: dayField.endTime
                ? dayjs(dayField.endTime).format("HH:mm")
                : null,
            });
            if(hTOneTime === null || hTBkTime === null) {
              let extraMinute = 0;
              if(values.travelTime) {
                extraMinute = values.travelTime;
              }
              hTOneTime = dayField.startTime
                ? dayjs(dayField.startTime).subtract(extraMinute, "minute").format("HH:mm")
                : null;
              hTBkTime = dayField.endTime
                ? dayjs(dayField.endTime).add(extraMinute, "minute").format("HH:mm")
                : null;
            }
          }
        });

        if (isJobSchedule) {
          recurringObj.workReleaseDestinations = [
            {
              workReleaseDestinationID: existingWRDId || null,
              businessID: existingBusId || null,
              comments: "",
              businesses: [
                {
                  businessId: existingBusId || null,
                  businessName: values.businessName || "",
                  businessPhone: values.businessPhone || "",
                  businessFax: values.businessFax || "",
                  businessDetails: [
                    {
                      businessDetailsId: existingBusDetailsId || null,
                      businessId: existingBusId || null,
                      businessPhone: values.businessPhone || "",
                      businessFax: values.businessFax || "",
                    },
                  ],
                  addresses: [
                    {
                      addressId: existingAddressId || null,
                      addressStreetNumber: values.businessHouseNumber || "",
                      addressStreet1: values.businessStreet || "",
                      addressStreet2: values.businessStreet2 || "",
                      addressApartmentNumber: values.businessApt || "",
                      addressCity: values.businessCity || "",
                      addressState: values.businessState || "",
                      addressZip: values.businessZip || "",
                    },
                  ],
                },
              ],
              workReleaseInspections: inspections,
            },
          ];
        }
        scheduledEventsRecurring.push(recurringObj);
      }

      formattedValues.scheduledEventsRecurring = scheduledEventsRecurring;

      if(hTOneTime === null || hTBkTime === null) {
        hTOneTime = formattedValues.oneTime;
        hTBkTime = formattedValues.oneTimeEventFinishes;
      }

      // Distinguish edit vs. new
      let rowKey = modalData ? getScheduledEventKey(modalData) : null;

      const houseTrackData = {};
      houseTrackData.actualDate= null;
      houseTrackData.actualTime = null;
      houseTrackData.approvingOfficerStaffID = !fullSearch ? userDetail?.staffId : null;
      const combined = [formattedValues.eventDescription, values.comments]
        .filter(Boolean)
        .join(" ");

      houseTrackData.comments = !fullSearch ? (combined || null) : null;

      houseTrackData.expectedBackDate = hTBkTimeDate;
      houseTrackData.expectedBackTime = hTBkTime;
      houseTrackData.scheduledDate = hTOneTimeDate;
      houseTrackData.scheduledTime = hTOneTime;
      houseTrackData.inmateLocation = "Loan";
      houseTrackData.loanType = scheduledEventOptions.find(
        (opt) => opt.value === values.scheduledEventTypeID
      )?.label;
      houseTrackData.movementStatus = "Pending";
      houseTrackData.bookingId = bookings.bookingId;
      houseTrackData.moveReason = "Routine Move";
      houseTrackData.loanDestination = "others - See comments";

      if (isEditing && rowKey) {
        const updatedList = localScheduledEvents?.map((item) => {
          const itemKey = getScheduledEventKey(item);
          if (itemKey === rowKey) {
            const existingTrackingId = item?.inmateTracking?.[0]?.inmateTrackingID || null;
            return {
              ...item,
              ...formattedValues,
              inmateTracking: [
                {
                  ...houseTrackData,
                  inmateTrackingID: existingTrackingId, // preserve ID
                },
              ],
            };
          }
          return item;
        });

        setLocalScheduledEvents(updatedList);
        dispatch(
          setBookings({
            ...bookings,
            scheduledEvents: updatedList,
          })
        );
      } else {
        const houseTrackArr = [houseTrackData];
        const newKey = `new-${Date.now()}`;
        const newItem = {
          id: newKey,
          inmateTracking: houseTrackArr,
          ...formattedValues,
        };
        const updatedList = [...localScheduledEvents, newItem];
        setLocalScheduledEvents(updatedList);
        dispatch(
          setBookings({
            ...bookings,
            scheduledEvents: updatedList,
          })
        );
      }

      // Close modal
      setShowScheduledEvents(false);
      setModalData(null);
      setIsEditing(false);
      form.resetFields();
    } catch (err) {
      console.error("Form error:", err);
    }
  };

  /** Close modal and reset form */
  const handleCloseModal = () => {
    setShowScheduledEvents(false);
    setModalData(null);
    setIsEditing(false);
    form.resetFields();
  };

  // Build table data
  const ScheduledEventsTable = {
    headers: [
      { key: "eventTypeLabel", text: "Event Type" },
      { key: "eventDescription", text: "Description" },
      { key: "displayRecurring", text: "Recurring" },
      { key: "LastScheduledDate", text: "Last Scheduled Date" },
      { key: "displayStartDate", text: "Start Date" },
      { key: "displayEndDate", text: "End Date" },
      { key: "businessName", text: "Business Name" },
      { key: "comments", text: "Comments" },
    ],
    rows: localScheduledEvents?.map((item) => {
      const eventTypeLabel = scheduledEventOptions.find(
        (opt) => opt.value === item.scheduledEventTypeID
      )?.label;

      // Extract Start Date, End Date, and Business Name from scheduledEventsRecurring
      const recurringData = item?.scheduledEventsRecurring?.[0] || {};
      const businessData =
        recurringData?.workReleaseDestinations?.[0]?.businesses?.[0] || {};

      return {
        ...item,
        eventTypeLabel: eventTypeLabel || "Unknown",
        displayRecurring: item.recurring ? "True" : "False",
        displayStartDate: item.recurring
          ? recurringData.startDate
            ? dayjs(recurringData.startDate, "MM/DD/YYYY").format("MM/DD/YYYY")
            : "-"
          : item.oneTimeDate
          ? dayjs(item.oneTimeDate).format("MM/DD/YYYY")
          : "-",
        displayEndDate: item.recurring
          ? recurringData.endDate
            ? dayjs(recurringData.endDate, "MM/DD/YYYY").format("MM/DD/YYYY")
            : "-"
          : item.oneTimeDate
          ? dayjs(item.oneTimeDate).format("MM/DD/YYYY")
          : "-",
        startDate: recurringData.startDate
          ? dayjs(recurringData.startDate, "MM/DD/YYYY").format("MM/DD/YYYY")
          : "-",
        endDate: recurringData.endDate
          ? dayjs(recurringData.endDate, "MM/DD/YYYY").format("MM/DD/YYYY")
          : "-",
        businessName: businessData.businessName || "-",
      };
    }),
  };
  useEffect(() => {
    if (isEditing) {
      let isShow = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      );
      if (isShow) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEditing]);
  return (
    <>
      {/* Buttons row */}
      <StyledGridRow justify={"space-between"}>
        <StyledGridCol>
          {
            <Button
              onClick={clickScheduledEvents}
              disabled={
                !bookingsFieldsEnabled ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              ADD
            </Button>
          }
        </StyledGridCol>
        <StyledGridCol>
          {
            <Button
              style={{ marginRight: "10px" }}
              onClick={handleEdit}
              disabled={
                !modalData ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.VIEW,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Details
            </Button>
          }
          {
            <Popconfirm
              title="Delete the Scheduled Event"
              description="Are you sure you want to delete this Scheduled Event?"
              onConfirm={handleDelete}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <Button
                disabled={
                  !bookingsFieldsEnabled ||
                  !modalData ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.DELETE,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  )
                }
              >
                Delete
              </Button>
            </Popconfirm>
          }
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          selected={!modalData}
          headers={ScheduledEventsTable.headers}
          data={ScheduledEventsTable.rows}
          style={{ marginTop: "15px" }}
          onRowClick={onRowClick}
        />
      )}

      <Modal
        header={"Scheduled Events"}
        show={showScheduledEvents}
        size="xl"
        onHide={handleCloseModal}
        footerActions={[
          {
            key: "save",
            text: "Save",
            onClick: handleSave,
            disabled: isDisabled,
          },
        ]}
        footerCloseAction={() => setIsEditing(false)}
        footerCloseActionText="Close"
        submitButtonDisabled={true}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changedValues) => {
            if (changedValues.oneTimeDateTime) {
              form.validateFields(["oneTimeEventFinishes"]);
            }
          }}
        >
          <BookingsSectionTitle>
            <span>Scheduled Event Information</span>
          </BookingsSectionTitle>

          <StyledGridRow gutter={16}>
            <StyledGridCol span={5}>
              <FormItem
                label="Event Type"
                name="scheduledEventTypeID"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Event Type is required",
                    },
                  ] : []
                }
              >
                <Select
                  id="eventType"
                  options={scheduledEventOptions}
                  placeholder="Event Type"
                  showSearch={false}
                  variant="outlined"
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={5}>
              <FormItem
                label="Description"
                name="eventDescription"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Description is required",
                    },
                    {
                      pattern: /^\S.*\S?$/,
                      message: "Description cannot have empty spaces",
                    },
                  ] : []
                }
              >
                <TextField disabled={!bookingsFieldsEnabled} />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={4}>
              <FormItem
                label="Recurring"
                name="recurring"
                layout="horizontal"
                labelPostion="label-right"
                style={{ paddingTop: "25px" }}
                valuePropName="checked"
              >
                <Checkbox
                  disabled={!bookingsFieldsEnabled || isJobSchedule}
                  checked={isJobSchedule || isRecurring}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={5}>
              <FormItem
                label="Travel Time(minutes)"
                name="travelTime"
                rules={
                  !fullSearch ?
                    [
                    { required: true, message: "Travel Time is required" },
                    {
                      type: "number",
                      transform: (value) =>
                        value ? Math.round(parseFloat(value)) : value,
                      message: "Travel time must be a number.",
                    },
                    {
                      validator: (_, value) =>
                        value <= 240
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                "The travel time cannot be longer than 4 hours"
                              )
                            ),
                    },
                    {
                      pattern: /^\S.*\S?$/,
                      message: "Travel time cannot have empty spaces",
                    },
                  ] : []
                }
              >
                <TextField
                  disabled={!bookingsFieldsEnabled}
                  onBlur={(e) => {
                    const { value } = e.target;
                    if (value) {
                      const rounded = Math.round(Number(value));
                      form.setFieldsValue({ travelTime: rounded });
                    }
                  }}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={5}>
              <FormItem label="Contact Name" name="contactName">
                <TextField disabled={!bookingsFieldsEnabled} />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          <BookingsSectionTitle>
            <span>Additional Event Information</span>
          </BookingsSectionTitle>
          <StyledGridRow>
            <StyledGridCol span={6}>
              <FormItem
                label="Sign Out Inmate"
                name="signOutInmateU"
                layout="horizontal"
                valuePropName="checked"
              >
                <Checkbox disabled={!bookingsFieldsEnabled} />
              </FormItem>
              {/* Hidden fields to store IDs if editing */}
              <FormItem name="scheduledEventExtraID" hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem name="scheduledEventID" hidden>
                <input type="hidden" />
              </FormItem>
              {/* Hidden fields for existing WorkReleaseDestinations IDs */}
              <FormItem name="existingWRDId" hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem name="existingBusId" hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem name="existingBusDetailsId" hidden>
                <input type="hidden" />
              </FormItem>
              <FormItem name="existingAddressId" hidden>
                <input type="hidden" />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          {/* One-Time */}
          {!isRecurring && (
            <>
              <BookingsSectionTitle>
                <span>One-Time Event Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                <StyledGridCol span={6}>
                  <FormItem
                    label="Scheduled Date/Time"
                    name="oneTimeDateTime"
                    required={!fullSearch}
                    rules={
                      !fullSearch
                      ? [
                        {
                          required: true,
                          message: "Scheduled Date/Time is required",
                        },
                      ] : []
                    }
                  >
                    <CommonDatePicker
                      placeholder="MM/DD/YYYY HH:mm"
                      format="MM/DD/YYYY HH:mm"
                      showTime={{ format: "HH:mm" }}
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={5}>
                  <FormItem
                    label="Time Event Finishes"
                    name="oneTimeEventFinishes"
                    dependencies={["oneTimeDateTime"]}
                    rules={
                      !fullSearch ?
                        [
                      ({ getFieldValue }) => ({
                        validator(_, finishTimeValue) {
                          const scheduleDateTime =
                            getFieldValue("oneTimeDateTime");
                          if (!scheduleDateTime) {
                            return Promise.reject(
                              new Error("Time Event Finish Time is required")
                            );
                          }
                          if (!finishTimeValue) {
                            const selectedEventType =
                              scheduledEventOptions.find(
                                (opt) =>
                                  opt.value ===
                                  getFieldValue("scheduledEventTypeID")
                              )?.label;
                            if (
                              [
                                "Activity Request",
                                "Medical",
                                "Job Schedule",
                              ].includes(selectedEventType)
                            ) {
                              return Promise.reject(
                                new Error("You must enter the end time")
                              );
                            }
                          }
                          const start = dayjs(scheduleDateTime);
                          const dateString = start.format("MM/DD/YYYY");
                          const finishTimeStr =
                            dayjs(finishTimeValue).format("HH:mm");
                          const finish = dayjs(
                            `${dateString} ${finishTimeStr}`,
                            "MM/DD/YYYY HH:mm"
                          );
                          if (!finish.isAfter(start)) {
                            return Promise.reject(
                              new Error(
                                "The finish time must be later than the scheduled time"
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                      ] : []
                    }
                  >
                    <TimePicker
                      format="HH:mm"
                      placeholder="HH:mm"
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          )}

          {/* Recurring */}
          {isRecurring && (
            <>
              <BookingsSectionTitle>
                <span>Recurring Event Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                <StyledGridCol span={4}>
                  <FormItem
                    label="Start Date"
                    name="startDate"
                    rules={
                      !fullSearch ? [
                      {
                        required: true,
                        message: "You must enter the start date",
                      },
                    ] : []}
                  >
                    <CommonDatePicker
                      format="MM/DD/YYYY"
                      placeholder="MM/DD/YYYY"
                      showTime={false}
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="End Date" name="endDate">
                    <CommonDatePicker
                      format="MM/DD/YYYY"
                      placeholder="MM/DD/YYYY"
                      showTime={false}
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={5}>
                  <FormItem
                    label="Active?"
                    name="activeRecurring"
                    layout="horizontal"
                    labelPostion="label-right"
                    style={{ paddingTop: "25px" }}
                    valuePropName="checked"
                  >
                    <Checkbox disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                {!isActiveRecurring && (
                  <>
                    <StyledGridCol span={5}>
                      <FormItem
                        label="Reactivation Date"
                        name="reactivationDate"
                      >
                        <CommonDatePicker
                          format="MM/DD/YYYY"
                          placeholder="MM/DD/YYYY"
                          showTime={false}
                          disabled={isActiveRecurring || !bookingsFieldsEnabled}
                        />
                      </FormItem>
                    </StyledGridCol>
                    <StyledGridCol span={5}>
                      <FormItem
                        label="Reactivation Comments"
                        name="reactivationComments"
                      >
                        <TextField
                          disabled={isActiveRecurring || !bookingsFieldsEnabled}
                        />
                      </FormItem>
                    </StyledGridCol>
                  </>
                )}
              </StyledGridRow>
              {/* Day-of-week schedule table with validations */}
              <StyledGridRow gutter={16}>
                <StyledGridCol>
                  <table cellPadding={10}>
                    <thead>
                      <tr>
                        <th></th>
                        <th>
                          <StyledTypographyText
                            color="var(--label-color-primary);"
                            fontSize="14px"
                            fontWeight="700"
                          >
                            Active
                          </StyledTypographyText>
                        </th>
                        <th>
                          <StyledTypographyText
                            color="var(--label-color-primary);"
                            fontSize="14px"
                            fontWeight="700"
                          >
                            Start Time
                          </StyledTypographyText>
                        </th>
                        <th>
                          <StyledTypographyText
                            color="var(--label-color-primary);"
                            fontSize="14px"
                            fontWeight="700"
                          >
                            End Time
                          </StyledTypographyText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                      ].map((day) => (
                        <tr key={day}>
                          <td>
                            <StyledTypographyText
                              color="var(--text-black);"
                              fontSize="14px"
                              fontWeight="700"
                            >
                              {day}
                            </StyledTypographyText>
                          </td>
                          <td>
                            <FormItem
                              name={[day, "active"]}
                              valuePropName="checked"
                            >
                              <Checkbox disabled={!bookingsFieldsEnabled} />
                            </FormItem>
                          </td>
                          <td>
                            <FormItem
                              name={[day, "startTime"]}
                              rules={
                                !fullSearch ? [
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      const dayData = getFieldValue(day);
                                      if (dayData && dayData.active && !value) {
                                        return Promise.reject(
                                          new Error(
                                            "You must enter the start time"
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ] : []
                              }
                            >
                              <TimePicker
                                format="HH:mm"
                                disabled={!bookingsFieldsEnabled}
                              />
                            </FormItem>
                          </td>
                          <td>
                            <FormItem
                              name={[day, "endTime"]}
                              rules={!fullSearch ? [
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    const dayData = getFieldValue(day);
                                    if (
                                      dayData &&
                                      dayData.active &&
                                      dayData.startTime &&
                                      !value
                                    ) {
                                      return Promise.reject(
                                        new Error("You must enter the end time")
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ] : []}
                            >
                              <TimePicker
                                format="HH:mm"
                                disabled={!bookingsFieldsEnabled}
                              />
                            </FormItem>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <FormItem
                    hidden={true}
                    name="scheduledDaysValid"
                    rules={!fullSearch ? [
                      {
                        validator: (_, value) => {
                          if (form.getFieldValue("activeRecurring")) {
                            const days = [
                              "Sunday",
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                              "Saturday",
                            ];
                            const valid = days.some((day) => {
                              const dayData = form.getFieldValue(day);
                              return dayData && dayData.active;
                            });
                            if (!valid) {
                              return Promise.reject(
                                new Error(
                                  "If this recurring event is Active, then you must enter a schedule for at least one day."
                                )
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ] : []}
                  >
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          )}

          <BookingsSectionTitle>
            <span>Other Information</span>
          </BookingsSectionTitle>
          <StyledGridRow gutter={16}>
            <StyledGridCol span={24}>
              <FormItem name="comments">
                <TextField
                  inputType="textarea"
                  as="textarea"
                  variant="outlined"
                  rows="3"
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          {/* Only for Job Schedule */}
          {isJobSchedule && (
            <>
              <BookingsSectionTitle>
                <span>Business Details</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                <StyledGridCol span={6}>
                  <FormItem
                    label="Business Name"
                    name="businessName"
                    rules={!fullSearch ? [
                      {
                        required: true,
                        message: "Business Name is required",
                      },
                      {
                        pattern: /^\S.*\S?$/,
                        message: "Business Name cannot have empty spaces",
                      },
                    ] : []}
                  >
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="Business Phone" name="businessPhone">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="Business Fax" name="businessFax">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow gutter={16}>
                <StyledGridCol span={6}>
                  <FormItem label="#" name="businessHouseNumber">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="Street" name="businessStreet">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow gutter={16}>
                <StyledGridCol span={6}>
                  <FormItem label="Apt#" name="businessApt">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="Street2" name="businessStreet2">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              <StyledGridRow gutter={16}>
                <StyledGridCol span={6}>
                  <FormItem label="City" name="businessCity">
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={2}>
                  <FormItem label="ST" name="businessState">
                    <Select
                      options={getOptionsFromRefList("USState")}
                      placeholder="ST"
                      showSearch={false}
                      variant="outlined"
                      disabled={!bookingsFieldsEnabled}
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem
                    label="Zip Code"
                    name="businessZip"
                    rules={!fullSearch ? [
                      {
                        pattern: /^[0-9-]{1,10}$/,
                        message:
                          "The zipcode field allows numbers and dashes only and must be 10 characters or less",
                      },
                    ] : []}
                  >
                    <TextField disabled={!bookingsFieldsEnabled} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              <BookingsSectionTitle>
                <span>Additional Business Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                <StyledGridCol span={4}>
                  <FormItem label="Contact Name" name="businessContactName">
                    <TextField disabled={true} />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol span={4}>
                  <FormItem label="Cell Phone" name="businessContactCell">
                    <TextField disabled={true} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>

              <ScheduledEventsInspections
                inspections={inspections}
                setInspections={setInspections}
              />
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default ScheduledEventsDetails;
